/*
 * Pure rules for the Firm Knowledge screens: what a row says, how the list is
 * filtered, which batches still want attention, what the upload dialog does
 * with the files it was handed. No React or IO so they stay unit-testable.
 */
import { DRAFT_TYPE_LABELS, type DraftType } from "../../api/types/draft";
import type {
  ConversionReport,
  EntryEdits,
  FirmLibraryBatch,
  FirmLibraryCategory,
  FirmLibraryConversion,
  FirmLibraryEntry,
  FirmLibrarySuggestion,
  MigrationStatus,
  SuggestionField
} from "./types";
import { canonicalVisa } from "./visaCatalog";

export const DOCX_MIME =
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
export const PDF_MIME = "application/pdf";

/** What the upload input accepts; mirrors the backend's allowed mimes. */
export const ACCEPTED_EXTENSIONS = [
  ".docx",
  ".doc",
  ".pdf",
  ".rtf",
  ".odt",
  ".pptx",
  ".xlsx",
  ".zip"
];

const MB = 1024 * 1024;

/** Largest document the library takes; mirrors the backend's `MAX_UPLOAD_BYTES`. */
export const MAX_UPLOAD_BYTES = 100 * MB;

/** Largest .zip (unpacked on the server); mirrors the backend's `MAX_ZIP_BYTES`. */
export const MAX_ZIP_BYTES = 400 * MB;

/** The name the file was uploaded under (no folder, no extension), when it
 * differs from the entry's name now; null when it was never renamed. */
export const renamedFrom = (
  entry: Pick<FirmLibraryEntry, "name" | "original_filename">
): string | null => {
  const file = (entry.original_filename ?? "").split("/").pop() ?? "";
  const dot = file.lastIndexOf(".");
  const stem = (dot > 0 ? file.slice(0, dot) : file)
    .replace(/\s+/g, " ")
    .trim();
  return stem && stem !== entry.name.trim() ? stem : null;
};

/** "DOCX", "PDF", "DOC"… from the mime type, falling back to the filename. */
export const formatLabel = (entry: {
  mime_type?: string | null;
  original_filename?: string | null;
  is_pdf?: boolean;
  authorable?: boolean;
}): string => {
  if (entry.is_pdf) return "PDF";
  if (entry.authorable) return "DOCX";
  const name = entry.original_filename ?? "";
  const dot = name.lastIndexOf(".");
  if (dot > 0 && dot < name.length - 1) {
    return name.slice(dot + 1).toUpperCase();
  }
  const mime = entry.mime_type ?? "";
  if (mime === PDF_MIME) return "PDF";
  if (mime === DOCX_MIME) return "DOCX";
  if (mime.includes("msword")) return "DOC";
  if (mime.includes("rtf")) return "RTF";
  if (mime.includes("opendocument.text")) return "ODT";
  if (mime.includes("presentation")) return "PPTX";
  if (mime.includes("spreadsheet")) return "XLSX";
  return mime.split("/").pop()?.toUpperCase() || "FILE";
};

export const formatBytes = (bytes: number | null | undefined): string => {
  if (!bytes || bytes <= 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/**
 * How an entry reads in the list: `Ready` once the agents can use it in full;
 * `Preparing` while it is being stored, rendered or read (an upload in intake
 * activates itself once the agent has described it); `Needs a description`
 * when the agent's read failed and the row waits for someone to write one;
 * `Needs attention` when the rendering failed (the file itself still mounts).
 */
export type EntryReadiness = "ready" | "preparing" | "attention" | "describe";

export const readinessOf = (entry: FirmLibraryEntry): EntryReadiness => {
  if (entry.status === "pending_review") {
    return entry.suggested?.status === "failed" ? "describe" : "preparing";
  }
  if (entry.status === "failed" || entry.rendering_status === "failed")
    return "attention";
  if (entry.rendering_status === "pending") return "preparing";
  if (entry.suggested?.status === "pending") return "preparing";
  return "ready";
};

export const READINESS_LABEL: Record<EntryReadiness, string> = {
  ready: "Ready",
  preparing: "Preparing",
  attention: "Needs attention",
  describe: "Needs a description"
};

// --- What a document is for --------------------------------------------------------

export const CATEGORIES: readonly FirmLibraryCategory[] = [
  "template",
  "sample",
  "authority",
  "playbook"
];

export const CATEGORY_LABEL: Record<FirmLibraryCategory, string> = {
  template: "Template",
  sample: "Sample Filing",
  authority: "Legal Authority",
  playbook: "Firm Playbook"
};

/** What the agents do with each kind; shown where the firm picks it. */
export const CATEGORY_DESCRIPTION: Record<FirmLibraryCategory, string> = {
  template:
    "A blank form or shell. Agents copy it and fill every blank from the case.",
  sample:
    "A filing from another matter. Agents follow its structure and voice, never its facts, and never cite it.",
  authority:
    "Law, a decision or agency guidance. Agents may cite it for what it says.",
  playbook:
    "The firm's own checklists and rules. Agents follow them; they are never cited."
};

/** Mirrors the backend's `category_of`: the firm's choice, else the tags, else a sample. */
export const categoryOf = (
  entry: Pick<FirmLibraryEntry, "category" | "effective_category" | "tags">
): FirmLibraryCategory => {
  if (entry.category) return entry.category;
  if (entry.effective_category) return entry.effective_category;
  if (entry.tags.includes("shell")) return "template";
  return "sample";
};

/** Another client's identifiers are in the document and nobody has dealt with them. */
export const hasOpenClientDetails = (
  entry: Pick<FirmLibraryEntry, "client_details">
): boolean => entry.client_details?.status === "found";

/** Live because intake read it, but nobody at the firm has looked at it yet. */
export const needsReview = (
  entry: Pick<FirmLibraryEntry, "auto_activated" | "reviewed_at" | "status">
): boolean =>
  entry.status === "active" && !!entry.auto_activated && !entry.reviewed_at;

/** True while something about the library is still moving on the server. */
export const shouldPoll = (input: {
  entries: FirmLibraryEntry[];
  batches: FirmLibraryBatch[];
  migration: MigrationStatus | null | undefined;
}): boolean => {
  if (input.migration?.status === "running") return true;
  if (input.batches.some((b) => b.status === "processing")) return true;
  return input.entries.some(
    (e) => readinessOf(e) === "preparing" || e.conversion?.status === "pending"
  );
};

// --- Editable copy of a PDF --------------------------------------------------------
// The library is docx-first: a PDF's copy that came through whole becomes the
// entry's file on its own, so a converted entry reads as a Word document (its
// icon says it came from a PDF). A copy with guessed parts waits for someone to
// check the listed spots and use it; until then, like an entry the copy could
// not be made for or one the firm sent back to its PDF, it stays a PDF.

/** True for a Word entry whose file is the copy made from an original PDF. */
export const convertedFromPdf = (
  entry: Pick<FirmLibraryEntry, "conversion">
): boolean => entry.conversion?.status === "promoted";

/**
 * What the row says about a PDF's conversion, or null when there is nothing
 * worth a badge (a native docx, a converted entry whose icon already says it,
 * a refusal the drawer explains). `check`: converted, but some of it was
 * guessed; `review`: a copy with guessed parts waits to be checked and used;
 * `kept_pdf`: the firm chose the PDF; `unused`: a clean copy exists but is
 * not the file (its promotion did not go through).
 */
export type ConversionBadgeKind =
  | "pending"
  | "check"
  | "review"
  | "kept_pdf"
  | "unused";

export const conversionBadgeOf = (
  entry: Pick<FirmLibraryEntry, "conversion">
): ConversionBadgeKind | null => {
  const { conversion } = entry;
  switch (conversion?.status) {
    case "pending":
      return "pending";
    case "promoted":
      return conversion.promoted_from === "review" ? "check" : null;
    case "ready":
      return conversion.kept_pdf ? "kept_pdf" : "unused";
    case "review":
      return conversion.kept_pdf ? "kept_pdf" : "review";
    default:
      return conversion?.kept_pdf ? "kept_pdf" : null;
  }
};

export const CONVERSION_BADGE_LABEL: Record<ConversionBadgeKind, string> = {
  pending: "Converting to Word",
  check: "Check Against PDF",
  review: "Word Copy to Review",
  kept_pdf: "Kept as PDF",
  unused: "Word Copy Not Applied"
};

export const CONVERSION_BADGE_TITLE: Record<ConversionBadgeKind, string> = {
  pending:
    "A Word copy is being made from this PDF. It becomes the document the agents copy and edit.",
  check:
    "Converted from a PDF, but some of it was guessed. Compare it with the original PDF.",
  review:
    "A Word copy is ready, but parts of it were guessed. Check the listed spots, then use it.",
  kept_pdf:
    "Your firm chose to keep the original PDF. The agents reproduce it instead of editing a Word copy.",
  unused:
    "A Word copy was made but could not be applied. Open the document to use it."
};

export const conversionRefusalText = (
  code: string | null | undefined,
  fallback?: string | null
): string => {
  switch (code) {
    case "scan":
      return "This PDF is a scan with no real text, so there is nothing to copy. Upload the Word file to make it editable.";
    case "form":
      return "This PDF is a fillable form; forms are filled in, not copied.";
    case "too_many_pages":
      return "This PDF is too long to copy in one piece. Upload the Word file instead.";
    case "encrypted":
      return "This PDF is encrypted, so its text cannot be read.";
    case "no_text":
    case "unreadable":
      return "This PDF has no text that can be read.";
    case "not_pdf":
      return "Only a PDF gets an editable copy.";
    default:
      return fallback || "No editable copy could be made from this PDF.";
  }
};

const AGENTS_USE_THE_PDF = "Agents use this PDF as filed.";

/** One sentence for the drawer, refusal codes included. */
export const conversionSummary = (
  conversion: FirmLibraryConversion | null | undefined
): string => {
  if (!conversion)
    return `No Word copy has been made yet. ${AGENTS_USE_THE_PDF}`;
  switch (conversion.status) {
    case "pending":
      return "A Word copy is being made from this PDF. Once it is ready, it becomes the document the agents copy and edit.";
    case "ready":
    case "review":
      if (conversion.kept_pdf)
        return `Your firm chose to keep the original PDF. ${AGENTS_USE_THE_PDF} A Word copy is ready if you want the agents to edit it instead.`;
      if (conversion.status === "review")
        return `A Word copy is ready, but parts of it were guessed. Check the spots below against the original PDF, then use the Word copy so the agents can copy and edit it. Until then, the agents use this PDF as filed.`;
      return `A Word copy is ready but was not applied. Use it to let the agents copy and edit it. Until then, the agents use this PDF as filed.`;
    case "promoted":
      return conversion.promoted_from === "review"
        ? "Converted from a PDF. Parts of it were guessed, so check the spots below against the original PDF. The agents copy and edit this Word document."
        : "Converted from a PDF; every word came through. The agents copy and edit this Word document, and the original PDF is kept beside it.";
    case "failed":
      return conversion.error
        ? `The PDF could not be converted to Word: ${conversion.error}. ${AGENTS_USE_THE_PDF}`
        : `The PDF could not be converted to Word. ${AGENTS_USE_THE_PDF}`;
    case "refused":
      return `${conversionRefusalText(
        conversion.code,
        conversion.error
      )} ${AGENTS_USE_THE_PDF}`;
    default:
      return "";
  }
};

export const percentLabel = (value: number | null | undefined): string | null =>
  value === null || value === undefined || Number.isNaN(value)
    ? null
    : `${Math.round(value * 100)}%`;

/** Human names for the report's block counters, in display order. */
export const CONVERSION_COUNT_LABEL: ReadonlyArray<[string, string]> = [
  ["paragraphs", "paragraphs"],
  ["headings", "headings"],
  ["list_items", "list items"],
  ["tables", "tables"],
  ["footnotes_placed", "footnotes"],
  ["images", "images"]
];

/**
 * A converter note in plain words, or null for the ones only an engineer
 * needs (repairs that worked, rejected table candidates).
 */
export const plainConversionNote = (note: string): string | null => {
  const text = note.trim();
  if (!text) return null;
  if (/were repaired|^table rejected/i.test(text)) return null;
  const rules: [RegExp, string | ((m: RegExpMatchArray) => string)][] = [
    [
      /^(\d+)% of the PDF's words reached the copy/i,
      (m) => `About ${100 - Number(m[1])}% of the words did not come through.`
    ],
    [
      /^only (\d+)% of the PDF's words reached the copy/i,
      (m) => `Only ${m[1]}% of the words came through.`
    ],
    [
      /^(\d+) word\(s\) still look mis-mapped \(([^)]*)\)/i,
      (m) =>
        `${m[1]} ${m[1] === "1" ? "word" : "words"} may be misread (${m[2]}).`
    ],
    [
      /deep heading level guessed/i,
      "A heading's level was guessed from its size."
    ],
    [
      /table rebuilt from drawn rules/i,
      "A table was rebuilt from the lines drawn on the page."
    ],
    [
      /numbered heading kept literal/i,
      "A numbered heading keeps its number as typed."
    ],
    [
      /structure tags did not line up/i,
      "Paragraph breaks were rebuilt from the page layout."
    ],
    [
      /mixed line spacing/i,
      "Line spacing varies; some paragraphs came out double-spaced."
    ],
    [
      /some pages carried no text/i,
      "Some pages have no text (images or blank pages)."
    ],
    [
      /(\d+) footnote\(s\) had no matching reference/i,
      (m) =>
        `${m[1]} ${
          m[1] === "1" ? "footnote" : "footnotes"
        } could not be matched to the text and were left out.`
    ],
    [
      /(\d+) horizontal rule\(s\) had no paragraph/i,
      (m) =>
        `${m[1]} horizontal ${
          m[1] === "1" ? "line was" : "lines were"
        } left out.`
    ],
    [
      /letterhead was laid out as a logo column/i,
      "The letterhead was laid out as a logo beside the text."
    ]
  ];
  for (const [pattern, out] of rules) {
    const match = text.match(pattern);
    if (match) return typeof out === "string" ? out : out(match);
  }
  return `${text[0].toUpperCase()}${text.slice(1)}${
    /[.!?]$/.test(text) ? "" : "."
  }`;
};

const BLOCK_KIND_LABEL: Record<string, string> = {
  heading: "Heading",
  paragraph: "Paragraph",
  list_item: "List item",
  quote: "Quotation",
  table: "Table"
};

/** One place in the copy worth checking against the original PDF. */
export type ConversionSpot = {
  /** 1-based page in the original PDF; null for whole-document notes. */
  page: number | null;
  text: string;
};

const MAX_SPOTS = 30;

/**
 * What an attorney should check before trusting the copy: whole-document
 * notes first, then each page with a guessed part, in page order.
 */
export const conversionSpots = (
  conversion: Pick<FirmLibraryConversion, "flags"> | null | undefined,
  report: ConversionReport | null | undefined
): ConversionSpot[] => {
  const notes = [...(conversion?.flags ?? []), ...(report?.flags ?? [])];
  const whole: ConversionSpot[] = [];
  const seen = new Set<string>();
  notes.forEach((note) => {
    const text = plainConversionNote(note);
    if (!text || seen.has(text)) return;
    seen.add(text);
    const page = note.match(/\bon page (\d+)/i);
    whole.push({ page: page ? Number(page[1]) : null, text });
  });
  // A few missing words without a coverage note are noise, not a spot.
  const lostWords = notes.some((note) => /words reached the copy/i.test(note));
  if (lostWords && report?.missing?.length) {
    whole.push({
      page: null,
      text: `Words to look for: ${report.missing.slice(0, 8).join(", ")}.`
    });
  }

  const byPage = new Map<number, string[]>();
  (report?.blocks ?? []).forEach((block) => {
    const { page } = block;
    if (!page || (block.confidence >= 0.8 && !block.flags?.length)) return;
    const reason = block.flags?.length
      ? block.flags.map((f) => plainConversionNote(f) ?? f).join(" ")
      : `${BLOCK_KIND_LABEL[block.kind] ?? "Text"} may not match the layout.`;
    const quote = block.text ? ` “${block.text.slice(0, 60).trim()}…”` : "";
    const list = byPage.get(page) ?? [];
    list.push(`${reason}${quote}`);
    byPage.set(page, list);
  });
  const pages = [...byPage.entries()]
    .sort(([a], [b]) => a - b)
    .map(([page, reasons]) => ({
      page,
      text:
        reasons.length === 1
          ? reasons[0]
          : `${reasons[0]} (and ${reasons.length - 1} more on this page)`
    }));
  return [...whole, ...pages].slice(0, MAX_SPOTS);
};

/** The status facet: a readiness, or live but nobody has looked at it yet. */
export type LibraryStatusFacet = EntryReadiness | "unreviewed";

export type LibraryFilter = {
  query: string;
  format: "all" | "docx" | "pdf" | "other";
  tag: string | null;
  kind: FirmLibraryCategory | null;
  visa: string | null;
  draftType: string | null;
  status: LibraryStatusFacet | null;
  /** Only documents that still carry another client's details. */
  clientDetails: boolean;
};

export const EMPTY_FILTER: LibraryFilter = {
  query: "",
  format: "all",
  tag: null,
  kind: null,
  visa: null,
  draftType: null,
  status: null,
  clientDetails: false
};

/** The pills the toolbar can add, in menu order. */
export type LibraryFacetKey =
  | "kind"
  | "visa"
  | "draftType"
  | "status"
  | "clientDetails"
  | "format"
  | "tag";

export const LIBRARY_FACETS: readonly LibraryFacetKey[] = [
  "kind",
  "visa",
  "draftType",
  "status",
  "clientDetails",
  "format",
  "tag"
];

export const STATUS_FACET_LABEL: Record<LibraryStatusFacet, string> = {
  ...READINESS_LABEL,
  unreviewed: "Not reviewed yet"
};

const STATUS_FACETS = Object.keys(STATUS_FACET_LABEL) as LibraryStatusFacet[];
const FORMATS: readonly LibraryFilter["format"][] = ["docx", "pdf", "other"];

/** The facet as one string for the pill and the URL; null when unset. */
export const facetValue = (
  filter: LibraryFilter,
  key: LibraryFacetKey
): string | null => {
  switch (key) {
    case "format":
      return filter.format === "all" ? null : filter.format;
    case "clientDetails":
      return filter.clientDetails ? "found" : null;
    default:
      return filter[key];
  }
};

export const setFacet = (
  filter: LibraryFilter,
  key: LibraryFacetKey,
  value: string | null
): LibraryFilter => {
  switch (key) {
    case "format":
      return {
        ...filter,
        format: FORMATS.includes(value as LibraryFilter["format"])
          ? (value as LibraryFilter["format"])
          : "all"
      };
    case "clientDetails":
      return { ...filter, clientDetails: value !== null };
    case "kind":
      return {
        ...filter,
        kind: CATEGORIES.includes(value as FirmLibraryCategory)
          ? (value as FirmLibraryCategory)
          : null
      };
    case "status":
      return {
        ...filter,
        status: STATUS_FACETS.includes(value as LibraryStatusFacet)
          ? (value as LibraryStatusFacet)
          : null
      };
    default:
      return { ...filter, [key]: value || null };
  }
};

export const activeFacets = (filter: LibraryFilter): LibraryFacetKey[] =>
  LIBRARY_FACETS.filter((key) => facetValue(filter, key) !== null);

export const isFiltering = (filter: LibraryFilter): boolean =>
  filter.query.trim() !== "" || activeFacets(filter).length > 0;

const FILTER_PARAM: Record<LibraryFacetKey | "query", string> = {
  query: "q",
  kind: "kind",
  visa: "visa",
  draftType: "type",
  status: "status",
  clientDetails: "client",
  format: "format",
  tag: "tag"
};

/** The filter a shared or reloaded URL asks for; unknown values are dropped. */
export const filterFromParams = (params: URLSearchParams): LibraryFilter =>
  LIBRARY_FACETS.reduce(
    (filter, key) => setFacet(filter, key, params.get(FILTER_PARAM[key])),
    { ...EMPTY_FILTER, query: params.get(FILTER_PARAM.query) ?? "" }
  );

/** `params` with the filter written over its own keys; everything else kept. */
export const writeFilterParams = (
  params: URLSearchParams,
  filter: LibraryFilter
): URLSearchParams => {
  const next = new URLSearchParams(params);
  const write = (name: string, value: string | null) => {
    if (value) next.set(name, value);
    else next.delete(name);
  };
  write(FILTER_PARAM.query, filter.query);
  for (const key of LIBRARY_FACETS) {
    write(FILTER_PARAM[key], facetValue(filter, key));
  }
  return next;
};

const formatBucket = (entry: FirmLibraryEntry): LibraryFilter["format"] => {
  if (entry.is_pdf) return "pdf";
  if (entry.authorable) return "docx";
  return "other";
};

/** Every visa an entry serves; older rows carry only `visa`. */
export const visasOf = (entry: {
  visa: string | null;
  visas?: string[];
}): string[] =>
  entry.visas?.length ? entry.visas : entry.visa ? [entry.visa] : [];

export const applyFilter = (
  entries: FirmLibraryEntry[],
  filter: LibraryFilter
): FirmLibraryEntry[] => {
  const q = filter.query.trim().toLowerCase();
  return entries.filter((entry) => {
    if (filter.format !== "all" && formatBucket(entry) !== filter.format)
      return false;
    if (filter.tag && !entry.tags.includes(filter.tag)) return false;
    if (filter.kind && categoryOf(entry) !== filter.kind) return false;
    if (filter.visa && !visasOf(entry).includes(filter.visa)) return false;
    if (filter.draftType && entry.draft_type !== filter.draftType) return false;
    if (filter.clientDetails && !hasOpenClientDetails(entry)) return false;
    if (filter.status) {
      const matches =
        filter.status === "unreviewed"
          ? needsReview(entry)
          : readinessOf(entry) === filter.status;
      if (!matches) return false;
    }
    if (!q) return true;
    const hay = [
      entry.name,
      entry.description,
      entry.guidance,
      entry.original_filename ?? "",
      entry.draft_type ?? "",
      ...visasOf(entry),
      ...entry.tags
    ]
      .join("\n")
      .toLowerCase();
    return hay.includes(q);
  });
};

/** Tags across the library, most used first, then alphabetical. */
export const collectTags = (entries: FirmLibraryEntry[]): string[] => {
  const counts = new Map<string, number>();
  for (const entry of entries) {
    for (const tag of entry.tags) counts.set(tag, (counts.get(tag) ?? 0) + 1);
  }
  return [...counts.entries()]
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .map(([tag]) => tag);
};

/** Every visa the library is filed under, most used first. */
export const collectVisas = (entries: FirmLibraryEntry[]): string[] => {
  const counts = new Map<string, number>();
  for (const entry of entries) {
    for (const visa of visasOf(entry))
      counts.set(visa, (counts.get(visa) ?? 0) + 1);
  }
  return [...counts.entries()]
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .map(([visa]) => visa);
};

/** The draft types the library covers, labelled, by label. */
export const collectDraftTypes = (
  entries: FirmLibraryEntry[]
): { value: string; label: string }[] =>
  [...new Set(entries.map((e) => e.draft_type).filter(Boolean) as string[])]
    .map((value) => ({
      value,
      label: DRAFT_TYPE_LABELS[value as DraftType] ?? value
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

const plural = (n: number, one: string, many: string) =>
  `${n} ${n === 1 ? one : many}`;

/** "In 4 drafts", "Read in 2 cases", or null when nothing has used it yet. */
export const usageLabel = (
  entry: Pick<FirmLibraryEntry, "draft_count" | "read_count">
): string | null => {
  const drafts = entry.draft_count ?? 0;
  const reads = entry.read_count ?? 0;
  if (drafts > 0) return `In ${plural(drafts, "draft", "drafts")}`;
  if (reads > 0) return `Read in ${plural(reads, "case", "cases")}`;
  return null;
};

/** The longer line for the drawer. */
export const usageSummary = (
  entry: Pick<FirmLibraryEntry, "draft_count" | "read_count">
): string => {
  const drafts = entry.draft_count ?? 0;
  const reads = entry.read_count ?? 0;
  if (!drafts && !reads) return "No agent has used it yet.";
  const parts = [];
  if (drafts) parts.push(`Started ${plural(drafts, "draft", "drafts")}`);
  if (reads)
    parts.push(
      `${drafts ? "read" : "Read"} by the agent in ${plural(
        reads,
        "case",
        "cases"
      )}`
    );
  return `${parts.join(", ")}.`;
};

export type BulkEdit =
  | { kind: "category"; category: FirmLibraryCategory }
  | { kind: "tags"; tags: string[] }
  | { kind: "visas"; visas: string[] };

/**
 * What one selected entry's PATCH carries for a bulk edit; null when it
 * already matches. Tags and visas are added, never replaced.
 */
export const bulkEditFor = (
  entry: FirmLibraryEntry,
  edit: BulkEdit
): EntryEdits | null => {
  switch (edit.kind) {
    case "category":
      return entry.category === edit.category
        ? null
        : { category: edit.category };
    case "tags": {
      const added = edit.tags.filter((t) => !entry.tags.includes(t));
      return added.length ? { tags: [...entry.tags, ...added] } : null;
    }
    case "visas": {
      const current = visasOf(entry);
      const added = edit.visas.filter((v) => !current.includes(v));
      return added.length ? { visas: [...current, ...added] } : null;
    }
    default:
      return null;
  }
};

/** SHA-256 of a file's bytes as lowercase hex, matching `upload_checksum`. */
export const sha256Hex = async (data: ArrayBuffer): Promise<string> => {
  const digest = await crypto.subtle.digest("SHA-256", data);
  return [...new Uint8Array(digest)]
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
};

/** The library entry already holding these exact bytes, by checksum. */
export const duplicateOf = (
  checksum: string,
  entries: Pick<FirmLibraryEntry, "id" | "name" | "upload_checksum">[]
) => entries.find((e) => e.upload_checksum === checksum) ?? null;

/** Newest first; entries without a timestamp sink to the bottom by name. */
export const sortEntries = (entries: FirmLibraryEntry[]): FirmLibraryEntry[] =>
  [...entries].sort((a, b) => {
    const ta = a.updated_at ?? a.created_at ?? "";
    const tb = b.updated_at ?? b.created_at ?? "";
    if (ta !== tb) return ta > tb ? -1 : 1;
    return a.name.localeCompare(b.name);
  });

// --- Sort menu ----------------------------------------------------------------

export type LibrarySortBy = "" | "name" | "updated" | "pages" | "used";
export type LibrarySortDir = "asc" | "desc";
export type LibrarySort = { sortBy: LibrarySortBy; sortDir: LibrarySortDir };

export const DEFAULT_SORT: LibrarySort = { sortBy: "", sortDir: "desc" };

export const LIBRARY_SORT_OPTIONS: ReadonlyArray<{
  value: LibrarySort;
  label: string;
}> = [
  { value: DEFAULT_SORT, label: "Default" },
  { value: { sortBy: "used", sortDir: "desc" }, label: "Most used" },
  { value: { sortBy: "name", sortDir: "asc" }, label: "Name (A-Z)" },
  { value: { sortBy: "name", sortDir: "desc" }, label: "Name (Z-A)" },
  { value: { sortBy: "updated", sortDir: "desc" }, label: "Updated (Newest)" },
  { value: { sortBy: "updated", sortDir: "asc" }, label: "Updated (Oldest)" },
  { value: { sortBy: "pages", sortDir: "desc" }, label: "Pages (Most)" },
  { value: { sortBy: "pages", sortDir: "asc" }, label: "Pages (Fewest)" }
];

/** `?sort=used-desc`; anything unknown is the default order. */
export const sortFromParam = (value: string | null): LibrarySort => {
  const [sortBy, sortDir] = (value ?? "").split("-");
  const match = LIBRARY_SORT_OPTIONS.find(
    (o) =>
      o.value.sortBy && o.value.sortBy === sortBy && o.value.sortDir === sortDir
  );
  return match ? match.value : DEFAULT_SORT;
};

export const sortParam = (sort: LibrarySort): string | null =>
  sort.sortBy ? `${sort.sortBy}-${sort.sortDir}` : null;

export const sortLabel = (sort: LibrarySort): string =>
  sort.sortBy
    ? LIBRARY_SORT_OPTIONS.find(
        (o) =>
          o.value.sortBy === sort.sortBy && o.value.sortDir === sort.sortDir
      )?.label ?? "Custom"
    : "Default";

/**
 * The sort the menu picked. "Default" keeps the incoming order (the hook's
 * `sortEntries`). Rows missing the sorted value sink to the bottom either way.
 */
export const sortLibrary = (
  entries: FirmLibraryEntry[],
  sort: LibrarySort
): FirmLibraryEntry[] => {
  if (!sort.sortBy) return entries;
  const dir = sort.sortDir === "asc" ? 1 : -1;
  const byName = (a: FirmLibraryEntry, b: FirmLibraryEntry) =>
    a.name.localeCompare(b.name);
  return [...entries].sort((a, b) => {
    switch (sort.sortBy) {
      case "name":
        return dir * byName(a, b);
      case "updated": {
        const ta = a.updated_at ?? a.created_at ?? null;
        const tb = b.updated_at ?? b.created_at ?? null;
        if (ta === tb) return byName(a, b);
        if (ta === null) return 1;
        if (tb === null) return -1;
        return dir * (ta > tb ? 1 : -1);
      }
      case "pages": {
        const pa = a.page_count ?? null;
        const pb = b.page_count ?? null;
        if (pa === pb) return byName(a, b);
        if (pa === null) return 1;
        if (pb === null) return -1;
        return dir * (pa - pb);
      }
      case "used": {
        const da = a.draft_count ?? 0;
        const db = b.draft_count ?? 0;
        if (da !== db) return dir * (da - db);
        const ra = a.read_count ?? 0;
        const rb = b.read_count ?? 0;
        if (ra !== rb) return dir * (ra - rb);
        return byName(a, b);
      }
      default:
        return 0;
    }
  });
};

/** Batches still in motion: being stored, or being read by the agent. */
export const openBatches = (batches: FirmLibraryBatch[]): FirmLibraryBatch[] =>
  batches.filter((b) => b.status === "processing" || b.status === "ready");

export const batchProgressLabel = (batch: FirmLibraryBatch): string => {
  const noun = batch.total === 1 ? "document" : "documents";
  switch (batch.status) {
    case "processing":
      return `Adding ${batch.processed} of ${batch.total} ${noun}…`;
    case "ready": {
      const kept = batch.entry_ids.length;
      const skipped = batch.skipped.length;
      const base = `The agent is reading ${kept} ${
        kept === 1 ? "document" : "documents"
      }…`;
      return skipped ? `${base} (${skipped} skipped)` : base;
    }
    case "confirmed":
      return "Added to the library";
    case "failed":
      return batch.error ? `Failed: ${batch.error}` : "Failed";
    default:
      return "";
  }
};

export const migrationLabel = (
  migration: MigrationStatus | null | undefined
): string | null => {
  if (!migration) return null;
  switch (migration.status) {
    case "running":
      return "Bringing over the templates your firm uploaded before…";
    case "failed":
      return "Some of your earlier templates could not be brought over. They are still safe; try again from the menu.";
    default:
      // Once the import is done the rows simply sit in the library; nothing to announce.
      return null;
  }
};

// --- Suggestions --------------------------------------------------------------

export const SUGGESTION_FIELDS: readonly SuggestionField[] = [
  "name",
  "description",
  "guidance",
  "draft_type",
  "visa",
  "tags",
  "category"
];

export const SUGGESTION_FIELD_LABEL: Record<SuggestionField, string> = {
  name: "Name",
  description: "Description",
  guidance: "Guidance",
  draft_type: "Draft type",
  visa: "Visa",
  tags: "Tags",
  category: "Kind"
};

/** Only a weak score is worth flagging beside a suggestion. */
export const isLowConfidence = (value: number | undefined): boolean =>
  value !== undefined && !Number.isNaN(value) && value < 0.5;

/** The form values the drawer edits, as the suggestion helpers see them. */
export type SuggestionFormValues = {
  name: string;
  description: string;
  guidance: string;
  tags: string[];
  draft_type: string | null;
  visas: string[];
  category: FirmLibraryCategory | null;
};

/** What a field would become if its suggestion were accepted; null when
 * the proposal is absent or already matches the current value. Tags merge:
 * only the tags the form lacks count, and accepting appends them. A
 * suggested visa joins the visas already picked. */
export const proposedValueFor = (
  values: SuggestionFormValues,
  suggestion: FirmLibrarySuggestion | null | undefined,
  field: SuggestionField
): string | string[] | null => {
  if (!suggestion || suggestion.status !== "ready") return null;
  switch (field) {
    case "tags": {
      const fresh = suggestion.tags.filter((tag) => !values.tags.includes(tag));
      return fresh.length ? fresh : null;
    }
    case "visa": {
      const proposed = suggestion.visa;
      if (!proposed) return null;
      const canonical = canonicalVisa(proposed) ?? proposed;
      return values.visas.includes(canonical) ? null : canonical;
    }
    case "draft_type":
    case "category": {
      const proposed = suggestion[field];
      if (!proposed) return null;
      return proposed === values[field] ? null : proposed;
    }
    default: {
      const proposed = (suggestion[field] ?? "").trim();
      if (!proposed) return null;
      return proposed === values[field].trim() ? null : proposed;
    }
  }
};

/**
 * The fields whose suggestion differs from what is in the form right now,
 * empty or not. This compares against live edits, so a filled field with a
 * different proposal still shows it.
 */
export const pendingSuggestions = (
  values: SuggestionFormValues,
  suggestion: FirmLibrarySuggestion | null | undefined
): SuggestionField[] =>
  SUGGESTION_FIELDS.filter(
    (field) => proposedValueFor(values, suggestion, field) !== null
  );

/** The form with one field's suggestion applied (text replaces, tags merge). */
export const applySuggestion = <T extends SuggestionFormValues>(
  values: T,
  suggestion: FirmLibrarySuggestion | null | undefined,
  field: SuggestionField
): T => {
  const proposed = proposedValueFor(values, suggestion, field);
  if (proposed === null) return values;
  if (field === "tags") {
    return { ...values, tags: [...values.tags, ...(proposed as string[])] };
  }
  if (field === "visa") {
    return { ...values, visas: [...values.visas, proposed as string] };
  }
  return { ...values, [field]: proposed as string };
};

/** The form with one suggested tag added. */
export const acceptSuggestedTag = <T extends SuggestionFormValues>(
  values: T,
  tag: string
): T =>
  values.tags.includes(tag)
    ? values
    : { ...values, tags: [...values.tags, tag] };

/** What Accept all would do, one line per field, for the preview. */
export const suggestionChanges = (
  values: SuggestionFormValues,
  suggestion: FirmLibrarySuggestion | null | undefined,
  fields: readonly SuggestionField[]
): { field: SuggestionField; label: string; value: string }[] =>
  fields.flatMap((field) => {
    const proposed = proposedValueFor(values, suggestion, field);
    if (proposed === null) return [];
    const label = SUGGESTION_FIELD_LABEL[field];
    let value: string;
    if (Array.isArray(proposed)) value = `add ${proposed.join(", ")}`;
    else if (field === "visa") value = `add ${proposed}`;
    else if (field === "category")
      value = CATEGORY_LABEL[proposed as FirmLibraryCategory] ?? proposed;
    else if (field === "draft_type")
      value = DRAFT_TYPE_LABELS[proposed as DraftType] ?? proposed;
    else value = proposed;
    return [{ field, label, value }];
  });

// --- Upload -------------------------------------------------------------------

const extensionOf = (name: string): string => {
  const dot = name.lastIndexOf(".");
  return dot >= 0 ? name.slice(dot).toLowerCase() : "";
};

export const isAcceptedFile = (file: { name: string }): boolean =>
  ACCEPTED_EXTENSIONS.includes(extensionOf(file.name));

/** Why a file can't go up (unsupported, or over the size limit), or null. */
export const rejectionOf = (file: {
  name: string;
  size: number;
}): string | null => {
  if (!isAcceptedFile(file)) return "Not a supported format";
  if (extensionOf(file.name) === ".zip") {
    return file.size > MAX_ZIP_BYTES
      ? `Zips over ${MAX_ZIP_BYTES / MB} MB can't be added`
      : null;
  }
  return file.size > MAX_UPLOAD_BYTES
    ? `Files over ${MAX_UPLOAD_BYTES / MB} MB can't be added`
    : null;
};

/** What goes up: every accepted file, one or many, zips included. */
export const acceptedFiles = (files: File[]): File[] =>
  files.filter((f) => rejectionOf(f) === null);

export const rejectedFiles = (files: File[]): File[] =>
  files.filter((f) => rejectionOf(f) !== null);

/** A first name for an upload: the filename without its extension. */
export const nameFromFilename = (filename: string): string => {
  const base = filename.split(/[\\/]/).pop() ?? filename;
  const dot = base.lastIndexOf(".");
  const stem = dot > 0 ? base.slice(0, dot) : base;
  return stem.replace(/[_-]+/g, " ").replace(/\s+/g, " ").trim();
};

/** Split "a, b; c" into clean unique tags. */
export const parseTags = (raw: string): string[] => {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const piece of raw.split(/[,;\n]/)) {
    const tag = piece.trim().toLowerCase().replace(/\s+/g, " ");
    if (tag && !seen.has(tag)) {
      seen.add(tag);
      out.push(tag);
    }
  }
  return out;
};

/**
 * Find the entry a retired Studio link points at: the new id, or the legacy
 * template id kept as `source_ref` (`custom_templates/<id>`).
 */
export const findRequestedEntry = (
  entries: FirmLibraryEntry[],
  requested: string | null | undefined
): FirmLibraryEntry | null => {
  if (!requested) return null;
  return (
    entries.find((e) => e.id === requested) ??
    entries.find(
      (e) =>
        e.source_ref === requested ||
        e.source_ref === `custom_templates/${requested}`
    ) ??
    null
  );
};
